/**
 * useQueryOrchestrator · Query-flow coordination hook.
 *
 * Owns the six smaller inline handlers plus wires up the two paired builder
 * modules (handleQuery · handlePermissionResponse) into a single hook API.
 * State remains in the parent · this hook is a pure coordinator taking every
 * dependency as a dep object argument.
 *
 * Extracted 2026-08-01 · IJ-246 · Phase 5D-2 of the KnowledgeGraphPage.jsx
 * split (see /app/memory/design_drafts/kg_page_split_map.md Section Three
 * Boundary Three · sub-slice 5D-2).
 * Umbrella: IJ-076 Predictive Text Rebuild continuation · Refactor
 * Programme 2026.
 * Owner: E1.
 *
 * Signature:
 *   const q = useQueryOrchestrator({ ...allDeps });
 *   const { handleQuery, handlePermissionResponse,
 *           detectQueryIntent, handleQueryInputChange,
 *           toggleSourceSelection, attemptProductSearch,
 *           submitPrimaryQuery, handleProductSearch } = q;
 *
 * Every handler body is transposed byte-verbatim from the pre-extraction
 * file. No behaviour change. Zero data-testid change. Zero URL change.
 */
import { useEffect } from 'react';
import { toast } from 'sonner';
import { API, authAxios } from '../utils/authClient';
import { classifyIntent } from '../lib/queryIntent';
import { recordSubmittedQuery } from '../lib/completionsCorpus';
import { buildHandleQuery } from './queryOrchestrator/handleQuery';
import { buildHandlePermissionResponse } from './queryOrchestrator/handlePermissionResponse';

export const useQueryOrchestrator = (rawDeps) => {
  // Destructure every dep at the top so the inline handler bodies below can
  // read and write those names identically to the original inline code.
  const {
    // State reads
    query, queryThread, quietMode, dataControls, selectedSources, queryIntent,
    detectedSubject, hasSeenQuietTip, firstTimeTip, followUpQuery,
    showSourceOptions, userLocation,
    intentTypingTimeout, refineDebounceTimer, refineRequestedQueries,
    // Setters
    setQuery, setQueryThread, setIsQuerying, setQueryIntent,
    setDetectedSubject, setFollowUpQuery, setAwaitingForecastFile,
    setQuietModeState, setSelectedSources, setShowSourceOptions,
    setDataControls, setAccuracyScore,
    setIntentTypingTimeout, setRefineDebounceTimer,
    setLocationConsentPrompt,
    accuracyMode,
    // Refs
    followUpRef, queryTextareaRef, handleQueryRef,
    // Parent-owned helpers
    handleQuotaExceededError, handleExportThread,
    incrementBilledCount, logSearchActivity, logMediaActivity,
    logLocationActivity, scrollToThreadItem, requestGeolocation,
    quietOnToast, quietOffToast,
    // From useQueryRefinement (Phase 5D-1)
    fetchRefinement,
  } = rawDeps;

  // Instantiate the authed axios client once. authAxios is a factory; calling
  // it returns an axios instance with the Bearer-token interceptor wired in.
  // Fixes 'api.post is not a function' crash — same regression as
  // useUploadFlow.js line 45. Root-caused 2026-08-11 during upload sweep.
  const api = authAxios();

  // ---- Six inline handlers · byte-verbatim from parent ----

  const detectQueryIntent = (text) => {
    const { isTransactional, intent } = classifyIntent(text);
    setQueryIntent(isTransactional ? intent : null);
  };

  const handleQueryInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    // Clear previous timeout
    if (intentTypingTimeout) {
      clearTimeout(intentTypingTimeout);
    }
    
    // Set new timeout for intent detection (debounce 500ms)
    const timeout = setTimeout(() => {
      detectQueryIntent(value);
    }, 500);
    setIntentTypingTimeout(timeout);

    // Session 3.6 — Real-time refinement. 1s pause → silently pre-warm
    // /query/refine so the Enter-press path is instant. The debounced
    // path NEVER pushes UI any more (D1 fix from May 2026 — fast typists
    // were seeing the clarification panel appear mid-sentence and their
    // input cleared from under them).
    if (refineDebounceTimer) clearTimeout(refineDebounceTimer);
    if (value.trim().length >= 4 && !refineRequestedQueries.has(value.trim())) {
      const t = setTimeout(() => { fetchRefinement(value, false); }, 1000);
      setRefineDebounceTimer(t);
    }
  };

  const toggleSourceSelection = (source) => {
    setSelectedSources(prev => {
      if (prev.includes(source)) {
        return prev.filter(s => s !== source);
      } else {
        return [...prev, source];
      }
    });
  };

  const attemptProductSearch = (q, sources) => handleProductSearch(q, sources);

  /*
   * SPD v5.86 hotfix (2026-02-14) · operator regression — "Buy running
   * shoes" magnifier-click did NOT fire the compare-search.
   *
   * Root cause: two separate submit paths on the primary input were
   * diverging.
   *   Path A — Enter keydown on the transparent textarea (L3348-3386)
   *            was routed through classifyIntent + attemptProductSearch
   *            for commerce queries (SPD v5.86 hotfix Fix 2).
   *   Path B — Magnifier button onClick (L3295) called
   *            `handleQuery(query, false)` DIRECTLY, bypassing the
   *            classifier and always taking the informational path.
   *
   * When the operator clicked the magnifier icon next to "Buy running
   * shoes", Path B fired the KG / web-search pipeline instead of the
   * compare product-search pipeline. That's why the operator saw the
   * "Thinking…" indicator (the informational path's spinner) but never
   * a compare-flavoured result.
   *
   * `submitPrimaryQuery()` is the single source of truth now. Both
   * Enter and magnifier click flow through it. Same race-proof
   * classifier fallback, same location-consent guard, same routing
   * decision. If the primary input evolves (voice-input mic, etc.)
   * every new entry-point wires up to this ONE helper.
   *
   * SPD v5.86 hotfix (2026-02-14) · ghost-prompt regression:
   * `useTypeahead(query)` was still churning in the background AFTER
   * submit fired, and when its LLM call finally returned 10-15 s
   * later it painted a smart-prompt on top of the already-running
   * search — the operator's WWII sequence: "search fired, results
   * loading; then a WWII prompt appeared below the query bubble;
   * when the result finally generated the prompt disappeared". Fix:
   * clear `query` SYNCHRONOUSLY the moment we commit to firing. The
   * hook's cleanup effect (`if (abortRef.current) abortRef.current.
   * abort()`) then aborts the in-flight typeahead request and no
   * ghost prompt ever paints. Do NOT clear on the location-consent
   * detour — user may dismiss the popup and we want the query text
   * preserved in the bar.
   */

  const submitPrimaryQuery = () => {
    const q = (query || '').trim();
    if (!q) return;
    recordSubmittedQuery(q);
    setQuery(''); // abort useTypeahead(query) so no ghost prompt paints.
    handleQuery(q, false);
  };

  const handleProductSearch = async (q, sources) => {
    if (!q.trim()) { toast.error('Please enter a query'); return; }
    setIsQuerying(true);
    // SPD v5.86 hotfix (2026-02-14) — clear the follow-up query
    // text as soon as we start the search. Was a regression: the
    // stale "Air Jordans High-top" query text lingered in the
    // follow-up input at the bottom of the thread after the search
    // fired, making the layout look like the grey result box was
    // ABOVE the query. Safe always — primary-input context has
    // followUpQuery empty already.
    setFollowUpQuery('');
    const productIdx = queryThread.length;

    // D16 fix (Feb 28, 2026) — push a loading placeholder BEFORE the
    // await so the user sees the "Searching..." grey card during the
    // request, matching the KG-query UX. Previously this branch awaited
    // the API call silently and only pushed the result on completion —
    // a click on Support Local / Big Players / Compare looked frozen.
    const modeLabel = sources.includes('compare') || (sources.includes('local') && sources.includes('online'))
      ? 'Comparing local retailers vs big players'
      : sources.includes('local') ? 'Searching local retailers' : 'Searching big players';
    const loadingResult = {
      answer: '', source_entities: [], source_documents: [], related_concepts: [],
      images: [], suggestions: [], version: '', citation_map: {},
      web_results: [], web_intro: '', has_kg_content: false,
      type: 'product_search',
      selectedSources: sources,
      results: [],
      progressMessage: modeLabel,
    };
    setQueryThread(prev => [...prev, { query: q, result: loadingResult, isStreaming: true }]);
    scrollToThreadItem(productIdx);

    // Cycle through honest progress messages so the user knows things
    // are happening. ~3 s per stage; product_search usually completes
    // in 5-15 s end-to-end. Cleared in the finally block.
    const stages = [
      modeLabel,
      'Filtering Canadian-first results',
      'Ranking by relevance',
      'Building your comparison',
    ];
    let stageIdx = 0;
    const stageTimer = setInterval(() => {
      stageIdx = Math.min(stageIdx + 1, stages.length - 1);
      setQueryThread(prev => prev.map((it, i) =>
        i === productIdx && it.isStreaming
          ? { ...it, result: { ...it.result, progressMessage: stages[stageIdx] } }
          : it
      ));
    }, 3000);

    try {
      const response = await api.post(`${API}/product_search`, {
        query: q,
        subject: detectedSubject || q,
        sources: sources,
        intent: queryIntent
      }, { timeout: 60000 });

      // Log search activity for data tracking (if user opted in)
      logSearchActivity(q);
      // Log media activity for product searches
      logMediaActivity();

      // Replace the placeholder in-place rather than appending — keeps
      // the index stable and prevents flicker.
      setQueryThread(prev => prev.map((it, i) => i === productIdx
        ? {
            query: q,
            result: {
              ...response.data,
              type: 'product_search',
              selectedSources: sources,
            },
            isStreaming: false,
          }
        : it
      ));

      setShowSourceOptions(false);
      setSelectedSources([]);
      setQueryIntent(null);
      setDetectedSubject('');

      // Sticky billable counter — transact queries (Support Local /
      // Big Players / Compare) hit /api/product_search and absolutely
      // count toward the 3-query quota. Was previously only ticked
      // inside handleQuery for KG queries — fix Feb 27, 2026 (T1+T2).
      incrementBilledCount();
    } catch (error) {
      // Remove the loading placeholder on error so the thread doesn't
      // show a perpetual "Searching..." card.
      setQueryThread(prev => prev.filter((_, i) => i !== productIdx));
      if (handleQuotaExceededError(error)) {
        // 402 already handled — Exit Banner fired, no further UI needed.
      } else {
        toast.error(error.response?.data?.detail || 'Failed to search products');
      }
    } finally {
      clearInterval(stageTimer);
      setIsQuerying(false);
      scrollToThreadItem(productIdx);
      // D7 fix — after a transact-button submit, move focus to the
      // follow-up input so the next keystroke lands there.
      setTimeout(() => {
        if (followUpRef.current) {
          try { followUpRef.current.focus({ preventScroll: true }); }
          catch { followUpRef.current.focus(); }
        } else if (queryTextareaRef.current) {
          queryTextareaRef.current.blur();
        }
      }, 200);
    }
  };


  // ---- Heavy handlers via paired builder modules ----

  const handleQuery = buildHandleQuery({
    ...rawDeps,
    attemptProductSearch,
    handleProductSearch,
  });

  const handlePermissionResponse = buildHandlePermissionResponse({
    ...rawDeps,
    handleQuery,
  });

  // Keep the parent-owned handleQueryRef.current pointing at the latest
  // handleQuery so keydown handlers (VoiceActivation · quiet-mode command
  // dispatchers) continue to fire correctly across re-renders.
  useEffect(() => {
    if (handleQueryRef) {
      handleQueryRef.current = handleQuery;
    }
  }, [handleQuery, handleQueryRef]);

  return {
    handleQuery,
    handlePermissionResponse,
    detectQueryIntent,
    handleQueryInputChange,
    toggleSourceSelection,
    attemptProductSearch,
    submitPrimaryQuery,
    handleProductSearch,
  };
};

export default useQueryOrchestrator;
